import React, { useState, useEffect } from 'react';
import { CoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import Row from 'components/Row';
import Text from 'components/Text';
import TutoringSelfServeViewBilingSessionDetails from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewBilling/TutoringSelfServeViewBillingSessionDetails';
import CoachingHeading from 'coaching/components/CoachingHeading';
import Display from 'components/Display';
import Image from 'components/Image';
import Illustration from 'assets/images/coaching/postpurchase_illustration.svg';
import Copy from 'components/Copy';
import MediaObject from 'components/MediaObject';
import List from 'components/List';
import ListItem from 'components/ListItem';
import Button, { Props as ButtonProps } from 'components/ButtonDeprecated';
import Link from 'components/Link';
import { TutoringSelfServeAddChildAccountModal } from 'coaching/components/CoachingSelfServeView/CoachingSelfServeAddChildAccountModal';
import Modal from 'react-modal';
import EventTracking from 'lib/EventTracking';
import { pushCustomEvent } from 'lib/googleTagManager';

import {
  getReservationOrThrow,
  getSelectedSubjectOrThrow,
  getUserTimezone,
  resolveChildName,
  getParticipantUuidOrThrow,
  resolvePurchaserEmail,
  saveLinkStudentCompleted,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';

export type Props = {
  goToNextState: () => void;
  restoredProps: {
    linkStudentToken: string;
  };
};

export type LinkStudentStorage = {
  finished: boolean;
};

export type ViewLinkStudentInputData = {
  selectedSubject: string;
  selectedUserTimezone: string;
  selectedReservation: CoachingReservation;
  childName?: string;
  purchaserUserEmail: string;
  participantUuid?: string;
};

export function retrieveDataForTutoringSelfServeLinkStudentView(): ViewLinkStudentInputData {
  return {
    selectedSubject: getSelectedSubjectOrThrow(),
    selectedReservation: getReservationOrThrow(),
    selectedUserTimezone: getUserTimezone(),
    childName: resolveChildName(),
    participantUuid: getParticipantUuidOrThrow(),
    purchaserUserEmail: resolvePurchaserEmail(),
  };
}

const eventPrefix = 'tutoring_self_schedule_purchase.link_student';

export default function TutoringSelfServeViewLinkStudent(props: Props) {
  useEffect(() => {
    pushCustomEvent('tutoring_link_student');
    EventTracking.trackKey(`${eventPrefix}.view`);
  }, []);

  const [showModal, setShowModal] = useState(false);
  const {
    selectedSubject,
    selectedReservation,
    selectedUserTimezone,
    childName,
    purchaserUserEmail,
    participantUuid,
  } = retrieveDataForTutoringSelfServeLinkStudentView();

  return (
    <>
      <Modal isOpen={showModal}>
        <TutoringSelfServeAddChildAccountModal
          onRequestHide={() => setShowModal(false)}
          onAddChild={() => {
            saveLinkStudentCompleted(true);
            props.goToNextState();
          }}
          participantUUID={participantUuid}
          currentUserEmail={purchaserUserEmail}
          linkStudentToken={props.restoredProps.linkStudentToken}
          eventKeyScreen="self_schedule_purchase"
        />
      </Modal>
      <Row size="medium">
        <Text size="medium">Your first session is set!</Text>
      </Row>
      <TutoringSelfServeViewBilingSessionDetails
        reservation={selectedReservation}
        userTimezone={selectedUserTimezone}
        studentName={childName}
        selectedSubject={selectedSubject}
        hideSessionFrequency={true}
      />
      <CoachingHeading>{`Invite ${childName}`} </CoachingHeading>
      <Row flush>
        <Display show="480">
          <Row size="xxlarge">
            <Text align="center">
              <Image src={Illustration} width={200} />
            </Text>
          </Row>
        </Display>
      </Row>
      <Row size="small">
        <Text size="xsmall">Only you have access to join sessions from this account.</Text>
      </Row>
      <Row size="small">
        <Copy size="xsmall">
          Invite your child to share access to Remind Tutoring and let them connect with the tutor
          and <b>join sessions from their own account.</b> You’ll still stay in the loop.
        </Copy>
      </Row>
      <Row size="small">
        <MediaObject figure="right" center>
          <Text color="graphite" size="medium">
            <Row size="xxxxsmall">
              <Text size="xxsmall" weight="semibold">
                Recommended for:
              </Text>
            </Row>
            <List size="small">
              <ListItem>
                <Text size="xsmall" color="black">
                  Students in middle or high school
                </Text>
              </ListItem>
              <ListItem>
                <Text size="xsmall" color="black">
                  Students with online assignments
                </Text>
              </ListItem>
            </List>
          </Text>
          <Display hide="480">
            <Image src={Illustration} width={200} />
          </Display>
        </MediaObject>
      </Row>
      <AccountLinkButtons
        addAccountSelected={() => setShowModal(true)}
        skipAccountLinking={() => {
          saveLinkStudentCompleted(true);
          props.goToNextState();
        }}
      />
    </>
  );
}

function AccountLinkButtons(props: {
  addAccountSelected: () => void;
  skipAccountLinking: () => void;
}) {
  const addAccountButtonProps: ButtonProps = {
    color: 'koolaid',
    level: 'primary',
    size: 'medium',
    'data-test': 'link-student-add-account-button',
    onClick: props.addAccountSelected,
    eventKey: `${eventPrefix}.add_account.click`,
  } as const;

  const notNowProps = {
    color: 'ink',
    underline: true,
    weight: 'semibold',
    'data-test': 'link-student-skip-linking',
    onClick: props.skipAccountLinking,
  } as const;

  return (
    <>
      <Display hide={'480'}>
        <MediaObject center>
          <Button {...addAccountButtonProps}>Invite student</Button>
          <Link {...notNowProps}>
            <Text size="small">Not now, continue with my account only</Text>
          </Link>
        </MediaObject>
      </Display>
      <Display show={'480'}>
        <Row size="small">
          <Button {...addAccountButtonProps} full>
            Invite student
          </Button>
        </Row>
        <Row>
          <Link {...notNowProps}>
            <Text align="center" size="small">
              Not now, continue with my account only
            </Text>
          </Link>
        </Row>
      </Display>
    </>
  );
}
